import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Icon from 'components/AppIcon';

const SuccessMetrics = () => {
  const navigate = useNavigate();

  const capabilities = [
    {
      id: 1,
      icon: "Package",
      value: "80+",
      suffix: " Products",
      label: "Premium Categories",
      description: "Specialized agricultural exports",
      color: "text-accent"
    },
    {
      id: 2,
      icon: "Globe",
      value: "50+",
      suffix: " Countries",
      label: "Global Reach Potential",
      description: "Worldwide shipping network",
      color: "text-primary"
    },
    {
      id: 3,
      icon: "Shield",
      value: "100%",
      suffix: " Quality",
      label: "Quality Guarantee",
      description: "Premium product standards",
      color: "text-success"
    },
    {
      id: 4,
      icon: "Clock",
      value: "24/7",
      suffix: " Support",
      label: "Customer Service",
      description: "Dedicated export assistance",
      color: "text-accent"
    }
  ];

  const ourCommitments = [
    {
      id: 1,
      type: "quality",
      message: "Premium agricultural products ready for export",
      time: "Our commitment",
      icon: "Award"
    },
    {
      id: 2,
      type: "processing",
      message: "Value-added processing for maximum quality",
      time: "Our specialty",
      icon: "Cog"
    },
    {
      id: 3,
      type: "service",
      message: "Dedicated support throughout export process",
      time: "Our promise",
      icon: "Users"
    }
  ];

  return (
    <section className="py-16 bg-gradient-to-r from-primary to-secondary text-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-full h-full bg-[url('data:image/svg+xml,%3Csvg width=%2260%22 height=%2260%22 viewBox=%220 0 60 60%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cg fill=%22none%22 fill-rule=%22evenodd%22%3E%3Cg fill=%22%23ffffff%22 fill-opacity=%220.1%22%3E%3Ccircle cx=%2230%22 cy=%2230%22 r=%222%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')]"></div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Capabilities Grid */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.2, ease: "easeOut" }}
              className="mb-8"
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Ready to Serve Global Markets
              </h2>
              <p className="text-white/90 text-lg">
                As a new company, we're positioned to deliver premium African agricultural products with modern standards and dedicated service.
              </p>
            </motion.div>

            <div className="grid grid-cols-2 gap-6">
              {capabilities.map((capability, index) => (
                <motion.div
                  key={capability.id}
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.8, delay: index * 0.15, ease: "easeOut" }}
                  className="bg-white/95 backdrop-blur-md p-6 text-center hover:scale-[1.02] transition-transform duration-500 ease-out rounded-2xl shadow-lg border border-white/30"
                >
                  <div className={`w-12 h-12 mx-auto mb-4 rounded-lg bg-primary flex items-center justify-center`}>
                    <Icon name={capability.icon} size={24} color="white" />
                  </div>
                  <div className={`text-3xl md:text-4xl font-bold mb-2 text-primary`}>
                    {capability.value}{capability.suffix}
                  </div>
                  <h3 className="font-semibold text-charcoal mb-1">{capability.label}</h3>
                  <p className="text-charcoal-600 text-sm">{capability.description}</p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Our Commitments */}
          <motion.div
            initial={{ opacity: 0, x: 15 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 1.0, delay: 0.3, ease: "easeOut" }}
            className="bg-white/95 backdrop-blur-md p-8 rounded-2xl shadow-lg border border-white/30"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-charcoal">Our Commitments</h3>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-success rounded-full animate-pulse"></div>
                <span className="text-charcoal-600 text-sm">Ready to serve</span>
              </div>
            </div>

            <div className="space-y-4">
              {ourCommitments.map((commitment, index) => (
                <motion.div
                  key={commitment.id}
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1, ease: "easeOut" }}
                  className="flex items-start space-x-4 p-4 bg-primary/10 rounded-lg backdrop-blur-sm border border-primary/20"
                >
                  <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon name={commitment.icon} size={18} color="white" />
                  </div>
                  <div className="flex-1">
                    <p className="text-charcoal font-medium mb-1">{commitment.message}</p>
                    <p className="text-charcoal-600 text-sm">{commitment.time}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Company Values */}
            <div className="mt-8 pt-6 border-t border-charcoal/20">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-2xl font-bold text-primary mb-1">100%</div>
                  <div className="text-charcoal-600 text-sm">Quality</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-accent mb-1">Fresh</div>
                  <div className="text-charcoal-600 text-sm">Products</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-secondary mb-1">Global</div>
                  <div className="text-charcoal-600 text-sm">Delivery</div>
                </div>
              </div>
            </div>

            {/* CTA Button */}
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate('/contact')}
              className="w-full mt-6 bg-primary hover:bg-primary-700 text-white py-4 px-6 rounded-lg font-semibold transition-colors duration-250 flex items-center justify-center space-x-2 shadow-lg"
            >
              <Icon name="MessageCircle" size={20} />
              <span>Start Your Export Journey</span>
            </motion.button>
          </motion.div>
        </div>

        {/* Bottom Company Capabilities */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, delay: 0.4, ease: "easeOut" }}
          className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-8 text-center"
        >
          <div className="flex flex-col items-center">
            <Icon name="Award" size={32} className="text-accent mb-3" />
            <div className="text-xl font-bold text-white mb-1">Premium Quality</div>
            <div className="text-white/80 text-sm">Guaranteed Standards</div>
          </div>
          <div className="flex flex-col items-center">
            <Icon name="Shield" size={32} className="text-success mb-3" />
            <div className="text-xl font-bold text-white mb-1">Fully Compliant</div>
            <div className="text-white/80 text-sm">Export Regulations</div>
          </div>
          <div className="flex flex-col items-center">
            <Icon name="Users" size={32} className="text-accent mb-3" />
            <div className="text-xl font-bold text-white mb-1">Dedicated Team</div>
            <div className="text-white/80 text-sm">Expert Support</div>
          </div>
          <div className="flex flex-col items-center">
            <Icon name="Truck" size={32} className="text-success mb-3" />
            <div className="text-xl font-bold text-white mb-1">Global Logistics</div>
            <div className="text-white/80 text-sm">Worldwide Shipping</div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default SuccessMetrics;